// =============================================================================
// Terminal Types Test Page - 测试终端类型选择器
// =============================================================================

import React, { useState } from 'react';
import TerminalTypeSelector from '../components/TerminalTypeSelector';
import CreateTerminalDialog from '../components/CreateTerminalDialog';
import TerminalTypeIndicator from '../components/TerminalTypeIndicator';
import ScriptSelector from '../components/ScriptSelector';
import ScriptExecutionMonitor from '../components/ScriptExecutionMonitor';
import TerminalManager from '../components/TerminalManager';
import ResourceCleanup from '../components/ResourceCleanup';
import TokenDebugger from '../components/TokenDebugger';

export default function TerminalTypesTestPage() {
  const [selectedType, setSelectedType] = useState('general');
  const [selectedScript, setSelectedScript] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [terminalConfig, setTerminalConfig] = useState(null);

  const handleCreateTerminal = async (config) => {
    console.log('🔧 Creating terminal with config:', config);
    setTerminalConfig(config);
    // 这里可以调用实际的API
    alert(`创建终端: ${JSON.stringify(config, null, 2)}`);
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">终端类型系统测试</h1>

        {/* 终端类型选择器测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">1. 终端类型选择器</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">完整模式</h3>
              <TerminalTypeSelector
                selectedType={selectedType}
                onTypeSelect={setSelectedType}
                showDescription={true}
              />
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">紧凑模式</h3>
              <TerminalTypeSelector
                selectedType={selectedType}
                onTypeSelect={setSelectedType}
                compact={true}
              />
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                当前选择的类型: <strong>{selectedType}</strong>
              </p>
            </div>
          </div>
        </div>

        {/* 终端类型指示器测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">2. 终端类型指示器</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {['general', 'service', 'ai_tool', 'development', 'monitoring', 'temporary'].map(type => (
              <div key={type} className="p-3 border rounded-lg">
                <div className="mb-2">
                  <TerminalTypeIndicator
                    terminalType={type}
                    persistenceLevel={type === 'service' || type === 'ai_tool' ? 'high' : 'standard'}
                    tags={[type, 'test']}
                    compact={false}
                  />
                </div>
                <div className="text-xs text-gray-500">类型: {type}</div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-3">紧凑模式指示器</h3>
            <div className="flex flex-wrap gap-2">
              {['general', 'service', 'ai_tool', 'development', 'monitoring', 'temporary'].map(type => (
                <TerminalTypeIndicator
                  key={type}
                  terminalType={type}
                  persistenceLevel={type === 'service' || type === 'ai_tool' ? 'high' : 'standard'}
                  compact={true}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 脚本选择器测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">3. 脚本选择器</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">完整模式</h3>
              <ScriptSelector
                selectedScript={selectedScript}
                onScriptSelect={setSelectedScript}
                terminalType={selectedType}
                showDescription={true}
              />
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">紧凑模式</h3>
              <ScriptSelector
                selectedScript={selectedScript}
                onScriptSelect={setSelectedScript}
                terminalType={selectedType}
                compact={true}
              />
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                当前选择的脚本: <strong>{selectedScript?.name || '无'}</strong>
              </p>
              {selectedScript && (
                <div className="mt-2 text-xs text-gray-500">
                  <div>ID: {selectedScript.id}</div>
                  <div>类别: {selectedScript.category}</div>
                  <div>终端类型: {selectedScript.terminalType}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 脚本执行监控测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">4. 脚本执行监控</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">完整模式</h3>
              <ScriptExecutionMonitor
                terminalId="test-terminal-id"
                compact={false}
              />
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">紧凑模式</h3>
              <ScriptExecutionMonitor
                terminalId="test-terminal-id"
                compact={true}
              />
            </div>
          </div>
        </div>

        {/* 终端管理测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">5. 终端管理</h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">完整模式</h3>
              <TerminalManager
                workspaceId="test-workspace-id"
                onTerminalSelect={(terminal) => console.log('Selected terminal:', terminal)}
                onTerminalDelete={(terminalId) => console.log('Deleted terminal:', terminalId)}
                compact={false}
              />
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">紧凑模式</h3>
              <TerminalManager
                workspaceId="test-workspace-id"
                onTerminalSelect={(terminal) => console.log('Selected terminal:', terminal)}
                onTerminalDelete={(terminalId) => console.log('Deleted terminal:', terminalId)}
                compact={true}
              />
            </div>
          </div>
        </div>

        {/* 资源清理测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">6. 资源清理</h2>

          <ResourceCleanup workspaceId="test-workspace-id" />
        </div>

        {/* 创建终端对话框测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">7. 创建终端对话框</h2>
          
          <div className="space-y-4">
            <button
              onClick={() => setShowDialog(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              打开创建终端对话框
            </button>

            {terminalConfig && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">最后创建的终端配置:</h4>
                <pre className="text-sm text-green-700 overflow-auto">
                  {JSON.stringify(terminalConfig, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* API测试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">8. API测试</h2>
          
          <div className="space-y-4">
            <button
              onClick={async () => {
                try {
                  console.log('🔍 Testing terminal types API...');
                  const response = await fetch('/api/terminals/types/info', {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    }
                  });
                  
                  console.log('📡 Response status:', response.status);
                  const data = await response.json();
                  console.log('📋 Response data:', data);
                  
                  alert(`API响应: ${JSON.stringify(data, null, 2)}`);
                } catch (error) {
                  console.error('❌ API test failed:', error);
                  alert(`API错误: ${error.message}`);
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              测试终端类型API
            </button>

            <button
              onClick={async () => {
                try {
                  console.log('🔍 Testing health API...');
                  const response = await fetch('/api/system/health');
                  const data = await response.json();
                  console.log('📋 Health data:', data);
                  alert(`健康检查: ${JSON.stringify(data, null, 2)}`);
                } catch (error) {
                  console.error('❌ Health check failed:', error);
                  alert(`健康检查错误: ${error.message}`);
                }
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              测试健康检查API
            </button>

            <button
              onClick={async () => {
                try {
                  console.log('🔍 Testing script templates API...');
                  const response = await fetch('/api/scripts/templates', {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    }
                  });
                  const data = await response.json();
                  console.log('📋 Script templates data:', data);
                  alert(`脚本模板: ${JSON.stringify(data, null, 2)}`);
                } catch (error) {
                  console.error('❌ Script templates test failed:', error);
                  alert(`脚本模板API错误: ${error.message}`);
                }
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              测试脚本模板API
            </button>

            <button
              onClick={async () => {
                try {
                  console.log('🔍 Testing resource stats API...');
                  const response = await fetch('/api/terminals/stats/resources', {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    }
                  });
                  const data = await response.json();
                  console.log('📊 Resource stats data:', data);
                  alert(`资源统计: ${JSON.stringify(data, null, 2)}`);
                } catch (error) {
                  console.error('❌ Resource stats test failed:', error);
                  alert(`资源统计API错误: ${error.message}`);
                }
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              测试资源统计API
            </button>

            <button
              onClick={async () => {
                const testTerminalId = prompt('请输入要删除的终端ID（测试用）:');
                if (!testTerminalId) return;

                try {
                  console.log(`🗑️ Testing terminal delete API for: ${testTerminalId}`);
                  const response = await fetch(`/api/terminals/${testTerminalId}`, {
                    method: 'DELETE',
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    }
                  });
                  const data = await response.json();
                  console.log('🗑️ Delete response:', data);
                  alert(`删除结果: ${JSON.stringify(data, null, 2)}`);
                } catch (error) {
                  console.error('❌ Terminal delete test failed:', error);
                  alert(`删除API错误: ${error.message}`);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              测试删除终端API
            </button>
          </div>
        </div>

        {/* Token调试 */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">9. Token调试</h2>

          <TokenDebugger />
        </div>

        {/* 调试信息 */}
        <div className="bg-gray-100 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">其他调试信息</h2>
          <div className="space-y-2 text-sm text-gray-600">
            <div>当前选择类型: {selectedType}</div>
            <div>当前选择脚本: {selectedScript?.name || '无'}</div>
            <div>当前URL: {window.location.href}</div>
            <div>API Base: {window.location.origin}</div>
          </div>
        </div>
      </div>

      {/* 创建终端对话框 */}
      <CreateTerminalDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        onCreateTerminal={handleCreateTerminal}
        workspaceId="test-workspace-id"
      />
    </div>
  );
}
